import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import {
  FeatureMappingOutput,
  FeatureQueryStatus,
  PreviewQueryRequestEnvironmentEnum,
} from "src/clients/features-control/api";
import { DropdownItem } from "src/design-system/Dropdown";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { useCapabilities } from "src/hooks/useCapabilities";

export type StatusToggleRequest = {
  queryId: string;
  environment: PreviewQueryRequestEnvironmentEnum;
  isActive: boolean;
  etag: string;
  entities: string[];
  event: string;
  features: FeatureMappingOutput[];
};

type StatusDropdownProps = {
  value: FeatureQueryStatus;
  disabled?: boolean;
  environment: PreviewQueryRequestEnvironmentEnum;
  queryId: string;
  etag: string;
  entities: string[];
  event: string;
  features: FeatureMappingOutput[];
  onRequestToggle: (request: StatusToggleRequest) => void;
};

type Status = {
  key: FeatureQueryStatus;
  value: string;
};

const STATUSES: Status[] = [
  {
    key: FeatureQueryStatus.ACTIVE,
    value: "Active",
  },
  {
    key: FeatureQueryStatus.INACTIVE,
    value: "Inactive",
  },
];

export const StatusDropdown: React.FC<StatusDropdownProps> = ({
  value,
  disabled = false,
  environment,
  queryId,
  etag,
  entities,
  event,
  features,
  onRequestToggle,
}) => {
  const { featureQueriesCatalogue } = useCapabilities();
  const isActive = value === FeatureQueryStatus.ACTIVE;
  const isLive = environment === PreviewQueryRequestEnvironmentEnum.LIVE;
  const canChangeStatus = isLive
    ? featureQueriesCatalogue.canChangeLiveStatus
    : featureQueriesCatalogue.canChangeSandboxStatus;

  return (
    <FixedPositionedDropdown<string, FeatureQueryStatus>
      buttonClassName="border-0 ring-0 focus:ring-0 "
      disabled={disabled || !canChangeStatus}
      elements={STATUSES.map((s) => ({
        ...s,
        disabled: s.key === value,
      }))}
      dataLoc={`${environment}-status-dropdown`}
      placement="bottom start"
      renderButtonValue={() => {
        const status = STATUSES.find((s) => s.key === value);
        return (
          <Tooltip
            body="You don't have permission to edit the status"
            disabled={canChangeStatus}
            placement="top"
            asChild
          >
            <div>
              <StatusBadge status={value} value={status?.value ?? ""} />
            </div>
          </Tooltip>
        );
      }}
      renderValue={({ value: displayValue, selected }) => (
        <DropdownItem
          data-loc={`status-${displayValue.toLowerCase()}`}
          data-selected={selected || undefined}
        >
          <span>{displayValue}</span>
        </DropdownItem>
      )}
      selected={value}
      onSelect={() =>
        onRequestToggle({
          queryId,
          environment,
          isActive,
          etag,
          entities,
          event,
          features,
        })
      }
    />
  );
};

const StatusBadge: React.FC<{
  status: FeatureQueryStatus;
  value: string;
}> = ({ status, value }) => {
  const isActive = status === FeatureQueryStatus.ACTIVE;

  return (
    <Pill size="sm" variant={isActive ? "green" : "gray"}>
      <Pill.Text>{value}</Pill.Text>
      <Pill.Icon icon={faChevronDown} />
    </Pill>
  );
};
